import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getClaimVolumes } from '../../api/claims';

const VolumeChart = ({ claimId }) => {
  const [chartType, setChartType] = useState('line'); // 'line' or 'bar'

  const { data, isLoading, error } = useQuery({
    queryKey: ['claimVolumes', claimId],
    queryFn: () => getClaimVolumes(claimId),
    enabled: !!claimId
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load volume data: {error.message}</p>
      </div>
    );
  }

  if (!data?.volumes || data.volumes.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-600">No volume data available</p>
      </div>
    );
  }

  const { volumes, summary } = data;

  // Format data for chart
  const chartData = volumes.map(v => ({
    period: v.period,
    'Actual Volume': v.actual_volume,
    'Contracted Volume': v.contracted_volume,
    'Forecast Volume': v.forecast_volume,
    'Budget Volume': v.budget_volume
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const variance = payload[0].payload['Contracted Volume'] - payload[0].payload['Actual Volume'];
      const variancePct = payload[0].payload['Contracted Volume'] > 0
        ? ((variance / payload[0].payload['Contracted Volume']) * 100).toFixed(2)
        : 0;

      return (
        <div className="bg-white border border-gray-300 rounded-lg p-3 shadow-lg">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
          <div className="border-t mt-2 pt-2">
            <p className={`text-sm font-semibold ${variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
              Variance: {variance.toLocaleString()} ({variancePct}%)
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Volume Comparison</h3>
          <p className="text-sm text-gray-600 mt-1">
            Actual vs Contracted vs Forecast over time
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setChartType('line')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              chartType === 'line'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Line
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              chartType === 'bar'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Bar
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-600 font-medium">Total Months</p>
          <p className="text-2xl font-bold text-blue-900">{summary.total_months}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3">
          <p className="text-xs text-red-600 font-medium">Total Variance</p>
          <p className="text-2xl font-bold text-red-900">{summary.total_variance.toLocaleString()}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-3">
          <p className="text-xs text-orange-600 font-medium">Avg Variance %</p>
          <p className="text-2xl font-bold text-orange-900">{summary.avg_variance_pct}%</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3">
          <p className="text-xs text-purple-600 font-medium">Trend</p>
          <p className="text-lg font-bold text-purple-900 capitalize">{summary.trend}</p>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={350}>
        {chartType === 'line' ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="Actual Volume"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="Contracted Volume"
              stroke="#ef4444"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="Forecast Volume"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
            <Line
              type="monotone"
              dataKey="Budget Volume"
              stroke="#a855f7"
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={{ r: 2 }}
            />
          </LineChart>
        ) : (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="Actual Volume" fill="#3b82f6" />
            <Bar dataKey="Contracted Volume" fill="#ef4444" />
            <Bar dataKey="Forecast Volume" fill="#10b981" />
            <Bar dataKey="Budget Volume" fill="#a855f7" />
          </BarChart>
        )}
      </ResponsiveContainer>

      {/* Legend Explanation */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-blue-500"></div>
            <span className="text-gray-600">Actual: Real volumes achieved</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-red-500 border-t-2 border-dashed border-red-500"></div>
            <span className="text-gray-600">Contracted: Agreed baseline</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-green-500"></div>
            <span className="text-gray-600">Forecast: Projected volumes</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-purple-500 border-t-2 border-dashed border-purple-500"></div>
            <span className="text-gray-600">Budget: Budgeted plan</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolumeChart;
